import { createSelector } from '@ngrx/store';

import { memoizeSelector } from '@core/ngrx-legacy';
import { TellentOrgAbilities, TellentOrgAbilitiesExtended } from '@core/tellent-types';

import { findCurrentMembership, TellentCurrentUserSelectors } from '../current-user';

// TEMP type for convenience; remove when TellentOrgAbilitiesExtended is no longer needed
type TellentOrgAllAbilities = TellentOrgAbilities | TellentOrgAbilitiesExtended;

const ALL_ABILITIES = <Record<TellentOrgAllAbilities, false>>{
  ...Object.fromEntries(Object.values(TellentOrgAbilities).map(ability => [ability, false])),
  ...Object.fromEntries(
    Object.values(TellentOrgAbilitiesExtended).map(ability => [ability, false]),
  ),
};

const abilitiesMapSelector = createSelector(
  TellentCurrentUserSelectors.data,
  function abilitiesMap(user): Record<TellentOrgAllAbilities, boolean> {
    const abilities = findCurrentMembership(user!)?.abilities;
    return abilities
      ? {
          ...ALL_ABILITIES,
          ...Object.fromEntries(
            abilities.map((ability: TellentOrgAllAbilities) => [ability, true]),
          ),
        }
      : ALL_ABILITIES;
  },
);

const hasOneSelector = memoizeSelector((ability: TellentOrgAllAbilities) =>
  createSelector(
    abilitiesMapSelector,
    function abilitiesHasOne(abilities: Record<TellentOrgAllAbilities, boolean>) {
      return abilities[ability];
    },
  ),
);

const hasManySelector = memoizeSelector((abilities: TellentOrgAllAbilities[]) =>
  createSelector(
    abilitiesMapSelector,
    function abilitiesHasMany(abilitiesMap: Record<TellentOrgAllAbilities, boolean>) {
      return abilities.every(ability => abilitiesMap[ability]);
    },
  ),
);

const hasAnySelector = memoizeSelector((abilities: TellentOrgAllAbilities[]) =>
  createSelector(
    abilitiesMapSelector,
    function abilitiesHasAny(abilitiesMap: Record<TellentOrgAllAbilities, boolean>) {
      return abilities.some(ability => abilitiesMap[ability]);
    },
  ),
);

export const TellentUserAbilitiesSelectors = {
  abilityMap: abilitiesMapSelector,
  hasOne: hasOneSelector,
  hasMany: hasManySelector,
  hasAny: hasAnySelector,
};
